import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Plane, ArrowLeft, Calculator, Database, Clock, 
  BarChart3, Users, Gauge, CheckCircle, Info
} from 'lucide-react';
import SEOHead, { buildBreadcrumb } from '@/components/SEOHead';
import AppLogo from '@/components/AppLogo';

export default function MethodologyPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <SEOHead
        title="How We Predict Airport Crowds — Methodology & Data Sources"
        description="A transparent look at the scoring model behind How Busy Is My Airport: live FlightAware data, aircraft capacity weighting, time-of-day, and user feedback calibration."
        path="/methodology"
        keywords="airport crowd prediction methodology, FlightAware data, airport algorithm, how to predict TSA wait times"
        structuredData={[
          buildBreadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Methodology', url: '/methodology' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'TechArticle',
            headline: 'How How Busy Is My Airport Predicts Crowd Levels',
            description: 'Real-time flight schedules + aircraft capacity weighting + feedback calibration = accurate arrival recommendations.',
            author: { '@type': 'Organization', name: 'How Busy Is My Airport' },
            publisher: { '@type': 'Organization', name: 'How Busy Is My Airport', url: 'https://howbusyismyairport.com' },
          },
        ]}
      />

      {/* Header */}
      <header className="container mx-auto px-4 sm:px-6 py-6">
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3 hover:opacity-80 transition-opacity min-w-0">
            <AppLogo size={36} className="flex-shrink-0" />
            <div className="leading-tight min-w-0">
              <h1 className="heading text-[13px] sm:text-base font-semibold tracking-tight text-slate-900 leading-[1.15]">
                How Busy Is My Airport
              </h1>
            </div>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Predictor
          </Link>
        </motion.div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-500 to-blue-600 text-white py-10 sm:py-14">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Calculator className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h1 className="text-3xl sm:text-4xl font-bold mb-3">Our Methodology</h1>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              How we predict airport crowd levels using real-time flight data and advanced algorithms
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto space-y-8">

          {/* Overview */}
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" />
                Overview
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Airport Busyness Predictor uses a proprietary algorithm that analyzes real-time flight 
                schedules to estimate crowd levels at airports. Our predictions help travelers make 
                informed decisions about when to arrive at the airport, reducing stress and avoiding 
                long security lines.
              </p>
              <p className="text-slate-600 leading-relaxed">
                We combine data from <strong>FlightAware's AeroAPI</strong>—one of the most comprehensive 
                flight tracking services in the world—with our own analysis of aircraft capacity, 
                historical patterns, and airport-specific factors.
              </p>
            </CardContent>
          </Card>

          {/* Data Sources */}
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-500" />
                Data Sources
              </h2>
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-slate-800 mb-2">FlightAware AeroAPI</h3>
                  <p className="text-slate-600 text-sm">
                    We use FlightAware's enterprise-grade AeroAPI to access real-time and scheduled 
                    flight data. This includes departure times, arrival times, aircraft types, 
                    airline information, and flight status for airports worldwide.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-semibold text-slate-800 mb-2">Aircraft Capacity Database</h3>
                  <p className="text-slate-600 text-sm">
                    We maintain a database of aircraft types and their typical passenger capacities. 
                    This allows us to weight flights by potential passenger volume, not just flight count.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-semibold text-slate-800 mb-2">Airport Classification Data</h3>
                  <p className="text-slate-600 text-sm">
                    Airports are classified by size and capacity (large hub, medium hub, small hub) 
                    based on FAA and IATA data. This helps normalize our predictions across different 
                    airport types.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* The Algorithm */}
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                The Prediction Algorithm
              </h2>
              
              <div className="space-y-6">
                {/* Step 1 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">Flight Data Collection</h3>
                    <p className="text-slate-600 text-sm">
                      We query all scheduled departures and arrivals within a 2-hour window of 
                      your specified time. This captures the flights most likely to impact your 
                      experience at security and the terminal.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">Aircraft Weighting</h3>
                    <p className="text-slate-600 text-sm mb-3">
                      Each flight is weighted based on its aircraft type and typical passenger capacity:
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                      <div className="p-2 bg-slate-100 rounded">
                        <span className="font-medium">Regional Jets</span>
                        <span className="block text-slate-500">CRJ, E175: 0.5x</span>
                      </div>
                      <div className="p-2 bg-slate-100 rounded">
                        <span className="font-medium">Narrow-body</span>
                        <span className="block text-slate-500">A320, 737: 1.0x</span>
                      </div>
                      <div className="p-2 bg-slate-100 rounded">
                        <span className="font-medium">Wide-body</span>
                        <span className="block text-slate-500">777, A350: 1.6x</span>
                      </div>
                      <div className="p-2 bg-slate-100 rounded">
                        <span className="font-medium">Large Wide-body</span>
                        <span className="block text-slate-500">A380, 747: 2.5x</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">Directional Analysis</h3>
                    <p className="text-slate-600 text-sm">
                      Departures are weighted at <strong>1.0x</strong> since departing passengers 
                      contribute directly to security and terminal congestion. Arrivals are weighted 
                      at <strong>0.3x</strong> as they primarily affect baggage claim and ground 
                      transportation areas.
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                    4
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">Airport Normalization</h3>
                    <p className="text-slate-600 text-sm mb-3">
                      Raw scores are normalized based on airport size to account for capacity differences:
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="p-2 bg-slate-100 rounded text-center">
                        <span className="font-medium block">Large Hub</span>
                        <span className="text-slate-500">÷ 3.0</span>
                      </div>
                      <div className="p-2 bg-slate-100 rounded text-center">
                        <span className="font-medium block">Medium Hub</span>
                        <span className="text-slate-500">÷ 1.5</span>
                      </div>
                      <div className="p-2 bg-slate-100 rounded text-center">
                        <span className="font-medium block">Small Hub</span>
                        <span className="text-slate-500">÷ 0.8</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                    5
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">Crowd Level Classification</h3>
                    <p className="text-slate-600 text-sm mb-3">
                      The normalized score is mapped to a crowd level:
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                      <div className="p-2 bg-blue-100 text-blue-800 rounded text-center">
                        <span className="font-medium block">Low</span>
                        <span>Score ≤ 5</span>
                      </div>
                      <div className="p-2 bg-indigo-100 text-indigo-800 rounded text-center">
                        <span className="font-medium block">Medium</span>
                        <span>Score 5-12</span>
                      </div>
                      <div className="p-2 bg-purple-100 text-purple-800 rounded text-center">
                        <span className="font-medium block">High</span>
                        <span>Score 12-20</span>
                      </div>
                      <div className="p-2 bg-violet-100 text-violet-800 rounded text-center">
                        <span className="font-medium block">Extreme</span>
                        <span>Score &gt; 20</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Arrival Time Recommendations */}
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-500" />
                Arrival Time Recommendations
              </h2>
              <p className="text-slate-600 mb-4">
                Based on the crowd level prediction, we recommend arrival times that account for:
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2 text-slate-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>Bag check vs. carry-on:</strong> Checking bags adds 15-30 minutes</span>
                </li>
                <li className="flex items-start gap-2 text-slate-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>International flights:</strong> Additional 45-60 minutes for customs/immigration</span>
                </li>
                <li className="flex items-start gap-2 text-slate-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>TSA PreCheck/CLEAR:</strong> Reduces recommended time by 10-15 minutes</span>
                </li>
              </ul>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-medium text-slate-700">Crowd Level</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-700">Carry-On</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-700">Checked Bags</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600">
                    <tr className="border-b border-slate-100">
                      <td className="py-2 px-3">Low</td>
                      <td className="py-2 px-3">75-90 min</td>
                      <td className="py-2 px-3">90-105 min</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-2 px-3">Medium</td>
                      <td className="py-2 px-3">90-105 min</td>
                      <td className="py-2 px-3">105-120 min</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-2 px-3">High</td>
                      <td className="py-2 px-3">105-120 min</td>
                      <td className="py-2 px-3">120-135 min</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3">Extreme</td>
                      <td className="py-2 px-3">135-150 min</td>
                      <td className="py-2 px-3">150-165 min</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Data Freshness */}
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Gauge className="w-5 h-5 text-blue-500" />
                Data Freshness & Confidence
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5"></div>
                  <div>
                    <p className="font-medium text-slate-800">Within 48 Hours: High Confidence</p>
                    <p className="text-sm text-slate-600">
                      We use live flight data from FlightAware. Schedules are confirmed and 
                      predictions are highly reliable.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-yellow-500 mt-1.5"></div>
                  <div>
                    <p className="font-medium text-slate-800">3-7 Days: Medium Confidence</p>
                    <p className="text-sm text-slate-600">
                      Mix of confirmed schedules and historical patterns. Good for planning, 
                      but recheck closer to travel date.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-100 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-slate-400 mt-1.5"></div>
                  <div>
                    <p className="font-medium text-slate-800">Beyond 7 Days: Estimated</p>
                    <p className="text-sm text-slate-600">
                      Based on historical patterns for that day/time. Useful for general planning 
                      but should be verified as your travel date approaches.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Limitations */}
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Limitations & Considerations
              </h2>
              <p className="text-slate-600 mb-4">
                While our predictions are based on comprehensive data, there are factors we cannot account for:
              </p>
              <ul className="space-y-2 text-slate-600 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-slate-400">•</span>
                  <span>TSA staffing levels, which vary by day and time</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400">•</span>
                  <span>Special events, conferences, or holidays that increase local travel</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400">•</span>
                  <span>Weather delays that cause flight bunching</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400">•</span>
                  <span>Security incidents or enhanced screening protocols</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400">•</span>
                  <span>Terminal-specific variations (some terminals busier than others)</span>
                </li>
              </ul>
              <p className="text-slate-600 mt-4 text-sm">
                We recommend using our predictions as a guide and always building in buffer time 
                for important flights.
              </p>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center py-6">
            <p className="text-slate-600 mb-4">Ready to see how busy your airport will be?</p>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plane className="w-5 h-5" />
              Check Airport Busyness
            </Link>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-4">
            <Link to="/" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Home</Link>
            <Link to="/airports" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Airport Guides</Link>
            <Link to="/blog" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Blog</Link>
            <Link to="/faq" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">FAQ</Link>
            <Link to="/methodology" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Methodology</Link>
            <Link to="/about" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">About</Link>
            <Link to="/privacy" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Privacy</Link>
            <Link to="/terms" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Terms</Link>
          </div>
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} Airport Busyness Predictor. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
